// ================================================================================
// RightSidebar.jsx — Full right sidebar with all synthesis panels
// ================================================================================

import React from "react";
import {
    PieChart, Fingerprint, Activity, Info, TrendingUp, Timer, Clock,
    Shield, Lightbulb, Zap, Diamond, BarChart, Layers, Crosshair, Flag,
    Download, FileText, CheckSquare, Megaphone, Key, AlertTriangle,
    DollarSign, TrendingDown, CheckSquare as CheckSq
} from "lucide-react";
import { formatChannelName, formatResourceValue, getPhaseAnalysis } from "./helpers";
import { ModernProgressBar } from "./SharedComponents";
import { encryptedFetch as fetch } from "../../../security";

const RightSidebar = ({
    results,
    wardAnalysis,
    wardContext,
    currentWardId,
    monteCarloRisks,
    monteCarloProjections,
    tacticalRecs,
    confidenceWarnings,
    potentialLevel,
    potentialPercent,
    potentialRationale,
    driver,
    driverRationale,
    campaignId,
    setActiveView,
    handleCreateReport
}) => {

    return (
        <div className="space-y-6">

            {/* Ward Action Box */}
            {wardAnalysis && (
                <div className="panel p-4 bg-slate-800/50 border border-accent-500/30 transition-all duration-300">
                    <h5 className="font-semibold text-white mb-3 truncate">Top 3 Actions for {wardAnalysis.ward_metadata.ward_name}</h5>
                    <ul className="space-y-3">
                        <li className="flex items-start space-x-3">
                            <div className="text-accent-400 pt-1"><CheckSquare size={16} /></div>
                            <div>
                                <span className="text-xs text-slate-400">Lead With</span>
                                <p className="text-sm font-semibold text-white">{formatChannelName(wardAnalysis.ranked_channels[0]?.channel)}</p>
                            </div>
                        </li>
                        <li className="flex items-start space-x-3">
                            <div className="text-accent-400 pt-1"><Megaphone size={16} /></div>
                            <div>
                                <span className="text-xs text-slate-400">Lead On </span>
                                <p className="text-sm font-semibold text-white">
                                    {(() => {
                                        const topIssue = wardContext?.ovid_messaging?.key_issues?.[0];
                                        if (!topIssue) return 'N/A';
                                        return typeof topIssue === 'object' ? (topIssue.issue || topIssue.description) : topIssue;
                                    })()}
                                </p>
                            </div>
                        </li>
                        <li className="flex items-start space-x-3">
                            <div className="text-accent-400 pt-1"><Key size={16} /></div>
                            <div>
                                <span className="text-xs text-slate-400">Intensity</span>
                                <p className="text-sm font-semibold text-white">{(wardContext?.artemis_strategy?.tier ?? 'N/A').replace(/_/g, ' ')}</p>
                            </div>
                        </li>
                    </ul>
                </div>
            )}

            {/* Resource Architecture */}
            <div className="panel p-4 bg-slate-800/80 border border-slate-700">
                <div className="flex items-center space-x-2 mb-3">
                    <PieChart size={16} className="text-emerald-400" />
                    <h5 className="font-semibold text-white">Resource Architecture</h5>
                </div>
                <div className="space-y-3">
                    {(() => {
                        const allocations = results?.campaign_optimization?.optimal_budget_allocation?.allocations ?? [];
                        const sortedAllocations = [...allocations].sort((a, b) => b.allocation_percentage - a.allocation_percentage);

                        if (sortedAllocations.length === 0) return <p className="text-xs text-slate-500">No allocation data generated.</p>;

                        return sortedAllocations.map(alloc => {
                            const isTime = alloc.resource_mode === 'time_only';
                            const isHybrid = alloc.resource_mode === 'hybrid';
                            const barColor = isTime ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-emerald-500';

                            return (
                                <div key={alloc.channel} className="group">
                                    <div className="flex justify-between items-center text-xs mb-1">
                                        <span className="font-medium text-slate-300 group-hover:text-white transition-colors truncate w-1/2" title={alloc.channel}>
                                            {formatChannelName(alloc.channel)}
                                        </span>
                                        <div className="text-right">
                                            {alloc.allocated_budget > 0 && <span className="block font-bold text-white">£{(alloc.allocated_budget).toLocaleString()}</span>}
                                            {alloc.allocated_hours > 0 && <span className="block font-bold text-blue-300">{alloc.allocated_hours.toFixed(0)} Hrs</span>}
                                        </div>
                                    </div>
                                    <div className="w-full bg-slate-700/50 rounded-full h-1.5 flex items-center gap-2">
                                        <div className={`${barColor} h-1.5 rounded-full`} style={{ width: `${alloc.allocation_percentage ?? 0}%` }}></div>
                                    </div>
                                    <div className="text-[10px] text-right text-slate-500 mt-0.5">{(alloc.allocation_percentage ?? 0).toFixed(1)}% Share</div>
                                </div>
                            );
                        });
                    })()}
                </div>
                <div className="mt-4 pt-3 border-t border-slate-700/50 flex flex-col gap-1">
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-slate-400">Total Capacity</span>
                        <span className="font-mono font-bold text-slate-300">£{(results?.input_summary?.budget ?? 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                        <span className="text-emerald-400 font-medium">Net Campaign Spend</span>
                        <span className="font-mono font-bold text-emerald-300">£{(results?.campaign_optimization?.optimal_budget_allocation?.total_budget_allocated ?? 0).toLocaleString()}</span>
                    </div>
                </div>
            </div>

            {/* Global Touchpoint Saturation */}
            {(Object.keys(results?.ward_level_analysis || {}).length > 0) && (
                <div className="panel p-5 bg-slate-800/80 border border-slate-700 relative overflow-hidden group flex flex-col items-center text-center">
                    <div className="w-full flex items-center justify-between mb-4 border-b border-slate-700/50 pb-3">
                        <div className="flex items-center space-x-2">
                            <Fingerprint size={16} className="text-slate-400" />
                            <h5 className="font-semibold text-white text-sm">Global Saturation</h5>
                        </div>
                        <span className="text-[10px] bg-slate-700 text-slate-300 px-2 py-0.5 rounded font-bold uppercase">Campaign Avg</span>
                    </div>
                    {(() => {
                        const wardIds = Object.keys(results?.ward_level_analysis || {});
                        const totalWards = wardIds.length || 1;
                        let campaignTotalSum = 0, campaignPhysicalSum = 0, campaignDigitalSum = 0;

                        wardIds.forEach(wid => {
                            const wData = results.ward_level_analysis[wid].touchpoint_analysis || {};
                            Object.values(wData).forEach(stats => {
                                const val = stats.avg_touchpoints || 0;
                                campaignTotalSum += val;
                                if (stats.is_physical) campaignPhysicalSum += val;
                                if (stats.is_digital) campaignDigitalSum += val;
                            });
                        });

                        const totalTouchpoints = campaignTotalSum / totalWards;
                        const physicalScore = campaignPhysicalSum / totalWards;
                        const digitalScore = campaignDigitalSum / totalWards;

                        let heroColor = "text-emerald-400", heroLabel = "Optimal Saturation";
                        if (totalTouchpoints < 5.0) { heroColor = "text-yellow-400"; heroLabel = "Low Saturation"; }
                        else if (totalTouchpoints > 12.0) { heroColor = "text-orange-400"; heroLabel = "High Saturation"; }

                        return (
                            <div className="w-full space-y-5">
                                <div>
                                    <div className={`text-4xl font-black ${heroColor} font-mono tracking-tighter mb-1`}>{totalTouchpoints.toFixed(1)}</div>
                                    <div className={`text-xs font-bold uppercase tracking-wider ${heroColor} mb-1`}>Avg. Touchpoints / Voter</div>
                                    <p className="text-[10px] text-slate-400 max-w-[200px] mx-auto leading-tight">{heroLabel}: Aggregate across all wards.</p>
                                </div>
                                <div className="grid grid-cols-2 gap-3 w-full pt-4 border-t border-slate-700/50">
                                    <div className="bg-slate-900/50 p-2 rounded border border-slate-700/50 flex flex-col items-center">
                                        <span className="text-[10px] uppercase text-slate-500 font-bold mb-1">Physical</span>
                                        <span className="text-lg font-bold text-white font-mono">{physicalScore.toFixed(1)}</span>
                                        <div className="w-full bg-slate-800 h-1 mt-2 rounded-full overflow-hidden">
                                            <div className="bg-blue-400 h-full" style={{ width: `${Math.min((physicalScore / totalTouchpoints) * 100, 100)}%` }}></div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-900/50 p-2 rounded border border-slate-700/50 flex flex-col items-center">
                                        <span className="text-[10px] uppercase text-slate-500 font-bold mb-1">Digital</span>
                                        <span className="text-lg font-bold text-white font-mono">{digitalScore.toFixed(1)}</span>
                                        <div className="w-full bg-slate-800 h-1 mt-2 rounded-full overflow-hidden">
                                            <div className="bg-purple-400 h-full" style={{ width: `${Math.min((digitalScore / totalTouchpoints) * 100, 100)}%` }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* Campaign Health Indicators */}
            <div className="panel p-0 bg-slate-800 border border-slate-700 shadow-sm rounded-lg overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-700 bg-slate-900/50 flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                        <Activity size={16} className="text-emerald-400" />
                        <h5 className="font-bold text-slate-200 text-sm">Campaign Health Indicators</h5>
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono uppercase">Live Risk Assessment</span>
                </div>

                <div className="p-5 space-y-6">
                    {/* Portfolio Stability */}
                    {(() => {
                        const variance = results?.campaign_optimization?.optimal_budget_allocation?.portfolio_risk_variance ?? 1.0;
                        const isStable = variance < 0.65;
                        const isConcentrated = variance > 0.85;
                        const stabilityPct = Math.max(0, Math.min(100, (1.0 - variance) * 125));

                        return (
                            <div>
                                <div className="flex justify-between items-end mb-2">
                                    <div>
                                        <div className="text-xs font-bold text-white mb-0.5">Portfolio Stability</div>
                                        <div className="text-[10px] text-slate-400">Risk Variance: <span className="font-mono text-slate-300">{variance.toFixed(3)}σ</span></div>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${isStable ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : isConcentrated ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'}`}>
                                        {isStable ? 'Diversified' : isConcentrated ? 'Critical Conc.' : 'Moderate'}
                                    </span>
                                </div>
                                <div className="w-full bg-slate-700/30 rounded-full h-2 mb-3 overflow-hidden">
                                    <div className={`h-full rounded-full transition-all duration-1000 ${isStable ? 'bg-gradient-to-r from-emerald-500 to-teal-400' : isConcentrated ? 'bg-gradient-to-r from-red-500 to-orange-400' : 'bg-gradient-to-r from-yellow-500 to-amber-400'}`} style={{ width: `${stabilityPct}%` }}></div>
                                </div>
                                <div className="bg-slate-900/50 p-2.5 rounded border border-slate-700/50 flex gap-2">
                                    <Info size={14} className="text-slate-500 mt-0.5 shrink-0" />
                                    <p className="text-[11px] text-slate-400 leading-snug">
                                        <span className="font-semibold text-slate-300">Analyst Insight:</span>
                                        {isStable
                                            ? " Portfolio is well-diversified. Impact is protected against single-channel failure."
                                            : isConcentrated
                                                ? " HIGH RISK. Allocation relies heavily on a single channel. Any friction there will collapse the campaign."
                                                : " Moderate concentration. Consider adding a secondary channel to hedge risk."}
                                    </p>
                                </div>
                            </div>
                        );
                    })()}

                    <div className="border-t border-slate-700/50"></div>

                    {/* Urgency & Scalability Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Voter Retention (Theta) */}
                        {(() => {
                            const theta = results?.performance_dashboard?.campaign_scorecard?.metrics?.time_decay_theta ??
                                results?.campaign_optimization?.sophisticated_research_metrics?.theta_decay_sensitivity ?? -0.3;
                            const isCritical = theta < -50;
                            const isUrgent = theta < -10;

                            return (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-white">Voter Retention</span>
                                        <Timer size={14} className={isCritical ? "text-red-500 animate-pulse" : isUrgent ? "text-rose-400" : "text-blue-400"} />
                                    </div>
                                    <div className="flex items-baseline gap-1 mb-2">
                                        <span className={`text-xl font-mono font-bold ${isCritical ? "text-red-500" : isUrgent ? "text-rose-400" : "text-white"}`}>{theta.toFixed(2)}</span>
                                        <span className="text-[10px] text-slate-500 uppercase">Decay Coeff.</span>
                                    </div>
                                    <div className="text-[10px] text-slate-400 leading-snug bg-slate-800/50 p-2 rounded border border-slate-700/50 h-20">
                                        {isCritical ? <span className="text-red-400 font-bold">CRITICAL DECAY.</span> : isUrgent ? <span className="text-rose-300 font-semibold">Decay Alert.</span> : <span className="text-blue-300 font-semibold">Stable Retention.</span>}
                                        <br />
                                        {isCritical ? "Immediate execution required. Voters are forgetting you daily." : isUrgent ? "Support evaporates rapidly without daily contact." : "Current support is sticky. You can afford gaps."}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Spend Scalability (Gamma) */}
                        {(() => {
                            const gamma = results?.performance_dashboard?.campaign_scorecard?.metrics?.saturation_risk_gamma ??
                                results?.campaign_optimization?.sophisticated_research_metrics?.gamma_saturation_risk ?? -0.056;
                            const isSaturated = gamma < -0.1;
                            const isAccelerating = gamma > 0.1;

                            return (
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-bold text-white">Spend Scalability</span>
                                        <TrendingUp size={14} className={isSaturated ? "text-yellow-400" : isAccelerating ? "text-emerald-400" : "text-slate-400"} />
                                    </div>
                                    <div className="flex items-baseline gap-1 mb-2">
                                        <span className={`text-xl font-mono font-bold ${isSaturated ? "text-yellow-400" : isAccelerating ? "text-emerald-400" : "text-white"}`}>{gamma.toFixed(3)}</span>
                                        <span className="text-[10px] text-slate-500 uppercase">Gamma</span>
                                    </div>
                                    <div className="text-[10px] text-slate-400 leading-snug bg-slate-800/50 p-2 rounded border border-slate-700/50 h-20">
                                        {isSaturated ? <span className="text-yellow-300 font-semibold">Diminishing Returns.</span> : isAccelerating ? <span className="text-emerald-300 font-semibold">Acceleration Phase.</span> : <span className="text-slate-300 font-semibold">Linear Growth.</span>}
                                        <br />
                                        {isSaturated ? "High saturation risk. Diversify channels immediately." : isAccelerating ? "High convexity. Increasing spend yields exponential returns." : "Stable. Increasing budget yields linear vote gains."}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>

            {/* Voter Retention Rate */}
            {(() => {
                const wards = results?.ward_level_analysis || {};
                const firstWardKey = Object.keys(wards)[0];
                const channelPerf = wards[firstWardKey]?.channel_performance || {};
                const firstChannelKey = Object.keys(channelPerf)[0];
                const decayEffect = channelPerf[firstChannelKey]?.contributing_factors?.diffusion_decay_effect ?? 1.0;
                const retentionPct = Math.min(100, Math.max(0, decayEffect * 100));
                const lossPct = 100 - retentionPct;
                const rawDays = results?.input_summary?.days_until_election ?? 0;
                const gapDays = results?.input_summary?.gap_days ?? 0;
                const displayDays = (gapDays > 0 && gapDays < rawDays) ? gapDays : rawDays;

                let status = { color: 'text-emerald-400', bar: 'bg-emerald-500', label: 'Excellent', icon: <CheckSq size={14} /> };
                if (retentionPct < 30) status = { color: 'text-red-400', bar: 'bg-red-500', label: 'Critical Decay', icon: <AlertTriangle size={14} /> };
                else if (retentionPct < 70) status = { color: 'text-yellow-400', bar: 'bg-yellow-500', label: 'Moderate Loss', icon: <Clock size={14} /> };

                return (
                    <div className="panel p-0 bg-slate-800 border border-slate-700 shadow-sm rounded-lg overflow-hidden">
                        <div className="px-4 py-2 border-b border-slate-700 bg-slate-900/50 flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                <Clock size={16} className={status.color} />
                                <h5 className="font-bold text-slate-200 text-sm">Voter Retention Rate</h5>
                            </div>
                            <span className="text-[10px] text-slate-500 font-mono uppercase">
                                {gapDays > 0 && gapDays < rawDays ? "Effective Horizon" : "Time to Election"}: <span className="text-white font-bold">{displayDays} Days</span>
                            </span>
                        </div>
                        <div className="p-4">
                            <div className="flex items-end justify-between mb-2">
                                <div>
                                    <div className={`text-2xl font-black font-mono ${status.color}`}>{retentionPct.toFixed(1)}%</div>
                                    <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Signal Retained</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-slate-500">-{lossPct.toFixed(1)}%</div>
                                    <div className="text-[10px] text-slate-600 uppercase font-bold tracking-wider">Decay Loss</div>
                                </div>
                            </div>
                            <div className="relative w-full h-3 bg-slate-700/50 rounded-full overflow-hidden mb-3">
                                <div className={`absolute left-0 top-0 bottom-0 ${status.bar} transition-all duration-1000 ease-out`} style={{ width: `${retentionPct}%` }}>
                                    <div className="absolute top-0 bottom-0 right-0 w-full bg-gradient-to-l from-white/20 to-transparent"></div>
                                </div>
                                <div className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)', backgroundSize: '10px 10px' }}></div>
                            </div>
                            <div className="flex gap-2 items-start bg-slate-900/50 p-2 rounded border border-slate-700/50">
                                <div className={`mt-0.5 ${status.color}`}>{status.icon}</div>
                                <p className="text-[11px] text-slate-300 leading-snug">
                                    <span className="font-semibold text-white">Impact Analysis: </span>
                                    {retentionPct < 30
                                        ? "Townsley Decay is severe. >70% of current persuasion efforts will fade before election day. Switch to 'Maintenance Mode'."
                                        : retentionPct < 70
                                            ? "Significant signal loss detected. Requires reinforcement (re-contact) closer to polling day."
                                            : "High retention window (Invernizzi Effect). Every contact made now is highly likely to convert into a vote."}
                                </p>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Untapped Potential */}
            <div className="panel p-4">
                <div className="flex items-center space-x-2 mb-2">
                    <Diamond size={16} className="text-cyan-400" />
                    <h5 className="font-semibold text-white">Untapped Potential</h5>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-300 mb-2">
                        {potentialLevel}
                        {potentialLevel !== 'N/A' && <span className="text-base font-normal text-cyan-400/70 ml-2">({(potentialPercent).toFixed(1)}% Elasticity)</span>}
                    </div>
                    <p className="text-xs text-slate-300">{potentialRationale}</p>
                </div>
            </div>

            {/* Strategic Driver */}
            <div className="panel p-4">
                <div className="flex items-center space-x-2 mb-3"><BarChart size={16} className="text-green-400" /><h5 className="font-semibold text-white">Strategic Driver</h5></div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-300 mb-2">{driver}</div>
                    <p className="text-xs text-slate-300">{driverRationale}</p>
                </div>
            </div>

            {/* Campaign Modifiers */}
            <div className="panel p-4">
                <div className="flex items-center space-x-2 mb-3"><Layers size={16} className="text-blue-400" /><h5 className="font-semibold text-white">Campaign Modifiers</h5></div>
                <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                        <div className="text-blue-400 pt-1"><Crosshair size={16} /></div>
                        <div>
                            <span className="text-xs text-slate-400">Focus</span>
                            <p className="text-sm font-medium text-white">{formatChannelName(results?.input_summary?.campaign_focus)}</p>
                            <p className="text-xs text-slate-300">Model is applying a positive weight to channels aligned with this strategy.</p>
                        </div>
                    </li>
                    <li className="flex items-start space-x-3">
                        <div className="text-blue-400 pt-1"><Flag size={16} /></div>
                        <div>
                            <span className="text-xs text-slate-400">Party</span>
                            <p className="text-sm font-medium text-white">{formatChannelName(results?.input_summary?.party)}</p>
                            <p className="text-xs text-slate-300">Applying adjustments based on party's historical demographic alignment.</p>
                        </div>
                    </li>
                    <li className="flex items-start space-x-3">
                        <div className="text-blue-400 pt-1"><Activity size={16} /></div>
                        <div>
                            <span className="text-xs text-slate-400">Campaign Phase</span>
                            {(() => {
                                const rawDays = results?.input_summary?.days_until_election ?? 30;
                                const gapDays = results?.input_summary?.gap_days ?? 0;
                                const effectiveDays = (gapDays > 0 && gapDays < rawDays) ? gapDays : rawDays;
                                const volatility = wardAnalysis?.demographic_intelligence?.demographic_volatility ?? 0.5;
                                const phase = getPhaseAnalysis(effectiveDays, volatility);
                                return (
                                    <>
                                        <p className="text-sm font-medium text-white">{phase.label}</p>
                                        <p className="text-xs text-slate-300">{phase.desc}</p>
                                        <div className="flex items-center space-x-2 mt-1">
                                            <span className={`text-[10px] px-1.5 rounded text-slate-300 ${gapDays > 0 && gapDays < rawDays ? 'bg-indigo-900/50 border border-indigo-500/30' : 'bg-slate-700'}`}>
                                                {effectiveDays} Days Left {gapDays > 0 && gapDays < rawDays ? '(Effective)' : ''}
                                            </span>
                                            {results?.input_summary?.electoral_shock && (
                                                <span className="text-[10px] bg-red-900/30 text-red-400 border border-red-900/50 px-1.5 rounded flex items-center gap-1">
                                                    <Zap size={8} /> Active Shock
                                                </span>
                                            )}
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </li>
                </ul>
            </div>

            {/* Executive Summary */}
            <div className="panel p-4">
                <div className="flex items-center space-x-2 mb-3"><Lightbulb size={16} className="text-yellow-400" /><h5 className="font-semibold text-white">Executive Summary</h5></div>
                <ul className="space-y-2 text-xs text-slate-300 list-disc list-inside">
                    {(results?.strategic_recommendations?.executive_summary ?? []).map((rec, i) => <li key={i}>{rec}</li>)}
                </ul>
            </div>

            {/* Actionable Insights */}
            <div className="panel p-4">
                <div className="flex items-center space-x-2 mb-3"><Zap size={16} className="text-success-400" /><h5 className="font-semibold text-white">Actionable Insights</h5></div>
                <ul className="space-y-2 text-xs text-slate-300 list-disc list-inside">
                    {(tacticalRecs.length > 0 ? tacticalRecs : []).slice(0, 4).map((rec, i) => <li key={i}>{rec}</li>)}
                    {tacticalRecs.length === 0 && <li>No actionable insights generated.</li>}
                </ul>
            </div>

            {/* Risk Management */}
            <div className="panel p-4">
                <div className="flex items-center space-x-2 mb-3"><Shield size={16} className="text-danger-400" /><h5 className="font-semibold text-white">Risk Management</h5></div>
                {monteCarloRisks ? (
                    <div className="space-y-2 text-xs text-slate-300">
                        <p className="font-medium text-white mb-1">Volatility Profile:</p>
                        <ul className="list-disc list-inside space-y-1">
                            <li>Volatility Index: {((monteCarloRisks.volatility_index ?? 0) * 100).toFixed(1)}%</li>
                            {monteCarloRisks.volatility_index > 0.15
                                ? <li><span className="text-yellow-400">High variance detected.</span> Diversify channels to mitigate risk.</li>
                                : <li>Stability is high. Projections are reliable.</li>}
                        </ul>
                    </div>
                ) : <p className="text-xs text-slate-500 mb-4">No specific risks identified.</p>}

                {confidenceWarnings.length > 0 && (
                    <div className="border-t border-slate-700 pt-3 mt-3">
                        <h6 className="text-sm font-semibold text-yellow-400 mb-2">Model Confidence Warnings</h6>
                        <ul className="space-y-2 text-xs text-yellow-300/80 list-disc list-inside">
                            {confidenceWarnings.map((warning, i) => <li key={i}>{warning}</li>)}
                        </ul>
                    </div>
                )}
            </div>

            {/* Model Notes */}
            {results?.monte_carlo_simulation?.error && (
                <div className="panel p-4 bg-yellow-500/10 border-yellow-500/20">
                    <div className="flex items-center space-x-2 mb-2"><Info size={16} className="text-yellow-400" /><h5 className="font-semibold text-white">Model Notes</h5></div>
                    <p className="text-xs text-yellow-300/80">{results.monte_carlo_simulation.error}</p>
                </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
                <button
                    onClick={() => {
                        fetch(`https://us-central1-knoesis-dante.cloudfunctions.net/reuseSimulatedCampaign?campaignID=${campaignId}&sessionId=${results.analysis_metadata.session_id}&reuse=true`, {});
                        setActiveView("campaignGallery");
                    }}
                    className="w-full flex items-center justify-center space-x-2 bg-accent-600 hover:bg-accent-700 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
                >
                    <Download size={20} /><span>Save Campaign</span>
                </button>
                <button
                    onClick={handleCreateReport}
                    className="w-full flex items-center justify-center space-x-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
                >
                    <FileText size={20} /><span>Create Report</span>
                </button>
            </div>
        </div>
    );
};

export default RightSidebar;
